import { Injectable } from '@angular/core';
import { LimitedCurrencyAmount } from '@lx/currency/shared/model/currency.model';
import { ComponentStore } from '@ngrx/component-store';

/**
 * State for quote-related data in Online Quote Tool
 * Simplified version compared to QT Lite since Online QT doesn't have extras
 */
export interface QuoteState {
  displayCurrency?: string;
  destinationCurrency?: string;
  grandTotal?: LimitedCurrencyAmount[];
  productTotal?: LimitedCurrencyAmount[];
  discountTotal?: LimitedCurrencyAmount[];
  discountTotalAsPercentage?: string;
}

/**
 * Store for quote-related data (pricing, discounts)
 * Separate from OnlineQuoteToolStore to follow separation of concerns
 */
@Injectable({ providedIn: 'root' })
export class QuoteStore extends ComponentStore<QuoteState> {
  // Selectors
  readonly displayCurrency$ = this.select(({ displayCurrency }) => displayCurrency);
  readonly destinationCurrency$ = this.select(({ destinationCurrency }) => destinationCurrency);
  readonly grandTotal$ = this.select(({ grandTotal }) => grandTotal);
  readonly productTotal$ = this.select(({ productTotal }) => productTotal);
  readonly discountTotal$ = this.select(({ discountTotal }) => discountTotal);
  readonly discountTotalAsPercentage$ = this.select(({ discountTotalAsPercentage }) => discountTotalAsPercentage);

  /**
   * Selector for Google Analytics dataLayer price data
   * Combines all pricing information needed for analytics tracking
   */
  readonly pricesSelectionForDataLayer$ = this.select(
    ({ productTotal, grandTotal, destinationCurrency, displayCurrency, discountTotalAsPercentage, discountTotal }) => ({
      productTotal: productTotal ?? grandTotal ?? [],
      grandTotal: grandTotal ?? [],
      destinationCurrency: destinationCurrency ?? displayCurrency,
      displayCurrency: displayCurrency ?? destinationCurrency,
      discountTotalAsPercentage,
      discountTotal: discountTotal ?? [],
    }),
    {
      debounce: true,
      equal: (previous, current) => previous.productTotal === current.productTotal && previous.grandTotal === current.grandTotal,
    },
  );

  // Updater
  readonly updateState = this.updater((state, partialState: Partial<QuoteState>) => ({
    ...state,
    ...partialState,
  }));

  constructor() {
    super({});
  }
}
